import {useOverlayDirection} from '@github-ui/copilot-chat-contexts/OverlayDirectionContext'
import {AUTO_MODEL_ID} from '@github-ui/copilot-chat-helpers/auto-model'
import {AUTO_TIER_METADATA, type AutoTier, type AutoTierMetadata} from '@github-ui/copilot-chat-helpers/auto-tiers'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {COPILOT_CHAT_MENU_PORTAL_ROOT} from '@github-ui/copilot-shared-ui/utils/portal-roots'
import type {AnchorSide} from '@primer/behaviors'
import {TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {memo, useCallback} from 'react'

import {useChatStateValues} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import styles from './AutoTierPicker.module.css'

export interface AutoTierPickerDesignProps {
  selectedAutoTier: AutoTier
  onAutoTierSelected: (autoTier: AutoTier) => void
  disabled?: boolean
  side?: AnchorSide
  position?: 'absolute' | 'relative'
  portalContainerName?: string
}

export function AutoTierPickerDesign({
  selectedAutoTier,
  onAutoTierSelected,
  disabled = false,
  side,
  position,
  portalContainerName,
}: AutoTierPickerDesignProps) {
  const selectedMetadata = AUTO_TIER_METADATA.find(metadata => metadata.value === selectedAutoTier)
  if (!selectedMetadata) return null

  return (
    <ActionMenu>
      <ActionMenu.Button
        aria-label={`Optimized for: ${selectedMetadata.label}`}
        className={styles.trigger}
        disabled={disabled}
        variant="invisible"
        trailingAction={TriangleDownIcon}
      >
        Optimized for: {selectedMetadata.label}
      </ActionMenu.Button>
      <ActionMenu.Overlay width="medium" side={side} position={position} portalContainerName={portalContainerName}>
        <ActionList selectionVariant="single" role="menu" aria-label="Optimized for">
          {AUTO_TIER_METADATA.map(metadata => (
            <AutoTierMenuItem
              key={metadata.value}
              metadata={metadata}
              selected={metadata.value === selectedAutoTier}
              onSelect={onAutoTierSelected}
            />
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

const AutoTierMenuItem = memo(function AutoTierMenuItemComponent({
  metadata,
  selected,
  onSelect,
}: {
  metadata: AutoTierMetadata
  selected: boolean
  onSelect: (autoTier: AutoTier) => void
}) {
  return (
    <ActionList.Item selected={selected} onSelect={() => onSelect(metadata.value)}>
      <span className={styles.label}>{metadata.label}</span>
      <ActionList.Description variant="block">{metadata.description}</ActionList.Description>
    </ActionList.Item>
  )
})

export function AutoTierPicker({disabled = false}: {disabled?: boolean}) {
  const {model, autoTier, mode, isWaitingOnCopilot, streamingMessage} = useChatStateValues(
    'model',
    'autoTier',
    'mode',
    'isWaitingOnCopilot',
    'streamingMessage',
  )
  const manager = useChatManager()
  const preferredOverlaySide = useOverlayDirection()
  const handleAutoTierSelected = useCallback(
    (selectedTier: AutoTier) => manager.selectAutoTier(selectedTier),
    [manager],
  )

  if (!copilotFeatureFlags.autoModeV2 || model.id !== AUTO_MODEL_ID) return null

  const isDisabled =
    disabled ||
    (copilotFeatureFlags.disableModelPickerWhileStreaming && (isWaitingOnCopilot || streamingMessage !== null))

  return (
    <AutoTierPickerDesign
      selectedAutoTier={autoTier}
      onAutoTierSelected={handleAutoTierSelected}
      disabled={isDisabled}
      side={preferredOverlaySide}
      position={mode === 'assistive' ? 'relative' : undefined}
      portalContainerName={mode === 'assistive' ? COPILOT_CHAT_MENU_PORTAL_ROOT : undefined}
    />
  )
}
